import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  Typography, 
  SvgIconProps, 
  styled,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Fade
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  Tooltip,
  Area
} from 'recharts';

export interface HistoricalDataPoint {
  /** Fecha o etiqueta del punto de datos */
  label: string;
  /** Valor numérico para este punto */
  value: number;
}

/** Tipo de periodos soportados */
export type PeriodType = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'custom';

export interface KpiChartCardProps {
  /** Título descriptivo de la estadística */
  title: string;
  /** Valor actual a mostrar con énfasis */
  value: string | number;
  /** Componente de icono de Material-UI */
  icon?: React.ComponentType<SvgIconProps>;
  /** Indicador de tendencia: positiva '+' o negativa '-' */
  trend?: '+' | '-';
  /** Texto adicional para la tendencia */
  trendText?: string;
  /** Datos históricos para el gráfico sparkline */
  historicalData: HistoricalDataPoint[];
  /** Determina si se muestra el selector de periodo */
  periodSelector?: boolean;
  /** Periodo inicial seleccionado */
  defaultPeriod?: PeriodType;
  /** Periodos disponibles para seleccionar (por defecto mes y año) */
  availablePeriods?: PeriodType[];
  /** Función que se ejecuta al cambiar el periodo */
  onPeriodChange?: (period: PeriodType) => void;
  /** Formato personalizado para los valores en el tooltip */
  valueFormatter?: (value: number) => string;
}

// Componente estilizado para el contenedor del icono con gradiente
const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 56,
  height: 56,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
  color: theme.palette.primary.contrastText,
  marginBottom: theme.spacing(2),
}));

// Componente estilizado para el indicador de tendencia
const TrendIndicator = styled(Box)<{ trend?: '+' | '-' }>(({ theme, trend }) => ({
  display: 'flex',
  alignItems: 'center',
  color: trend === '+' ? theme.palette.success.main : trend === '-' ? theme.palette.error.main : 'inherit',
  fontSize: '0.875rem',
  fontWeight: 500,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

// Mapeo de etiquetas para mostrar en el selector de periodo
const periodLabels: Record<string, string> = {
  day: 'Día',
  week: 'Semana',
  month: 'Mes',
  quarter: 'Trimestre',
  year: 'Año',
  custom: 'Personalizado'
};

// Componente para el tooltip personalizado
const CustomTooltip = ({ active, payload, formatter }: any) => {
  if (active && payload && payload.length) {
    const value = payload[0].value;
    const formattedValue = formatter ? formatter(value) : 
      typeof value === 'number' ? 
        new Intl.NumberFormat('es-ES', {
          style: value > 1000 ? 'currency' : 'decimal',
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value) : 
        value;
        
    return (
      <Box
        sx={{
          backgroundColor: 'background.paper',
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          minWidth: '100px',
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {payload[0].payload.label}
        </Typography>
        <Typography 
          variant="body1" 
          fontWeight="bold" 
          color="text.primary"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {formattedValue}
        </Typography>
      </Box>
    );
  }
  return null;
};

/**
 * Componente KpiChartCard para mostrar estadísticas con gráfico sparkline
 * 
 * Combina un StatCard con un gráfico de línea pequeño y soporte para selector de periodo
 */
export const KpiChartCard = ({
  title,
  value,
  icon: Icon,
  trend,
  trendText,
  historicalData,
  periodSelector = false,
  defaultPeriod = 'month',
  availablePeriods,
  onPeriodChange,
  valueFormatter
}: KpiChartCardProps) => {
  const [period, setPeriod] = useState<PeriodType>(defaultPeriod);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [visibleData, setVisibleData] = useState(historicalData);
  
  // Determina los periodos disponibles para seleccionar
  const periods = availablePeriods || ['month', 'year'];

  // Efecto para animar la transición de datos cuando cambia el periodo
  useEffect(() => {
    // Actualizar los datos según el periodo actual
    if (onPeriodChange) {
      setIsTransitioning(true);
      
      // Ocultar datos brevemente durante la transición
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [period, onPeriodChange]);

  // Actualizar los datos visibles cuando cambian los historicalData
  useEffect(() => {
    setVisibleData(historicalData);
  }, [historicalData]);

  const handlePeriodChange = (event: SelectChangeEvent) => {
    const newPeriod = event.target.value as PeriodType;
    setPeriod(newPeriod);
    if (onPeriodChange) {
      onPeriodChange(newPeriod);
    }
  };

  // Color para la línea del gráfico basado en la tendencia
  const lineColor = trend === '+' 
    ? '#4caf50' // success.main color 
    : trend === '-' 
      ? '#f44336' // error.main color
      : '#1976d2'; // primary.main color

  // ID único para el gradiente del área (para evitar conflictos cuando hay múltiples gráficos)
  const gradientId = `colorValue-${title.replace(/\s+/g, '')}`;

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: 2,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        {Icon && (
          <IconContainer>
            <Icon sx={{ fontSize: 28 }} />
          </IconContainer>
        )}

        {periodSelector && (
          <FormControl size="small" variant="outlined" sx={{ minWidth: 100 }}>
            <Select
              value={period}
              onChange={handlePeriodChange}
              displayEmpty
              inputProps={{ 'aria-label': 'period-selector' }}
            >
              {periods.map((p) => (
                <MenuItem key={p} value={p}>
                  {periodLabels[p]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <Typography variant="h6" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      
      <Fade in={!isTransitioning} timeout={300}>
        <Typography variant="h4" component="div" fontWeight={600}>
          {value}
        </Typography>
      </Fade>
      
      {trend && (
        <Fade in={!isTransitioning} timeout={300}>
          <TrendIndicator trend={trend}>
            {trend === '+' ? (
              <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} />
            ) : (
              <TrendingDownIcon fontSize="small" sx={{ mr: 0.5 }} />
            )}
            {trendText || (trend === '+' ? 'Incremento' : 'Decremento')}
          </TrendIndicator>
        </Fade>
      )}

      {/* Gráfico sparkline */}
      <Fade in={!isTransitioning} timeout={400}>
        <Box sx={{ height: 60, mt: 'auto' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={visibleData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={lineColor} stopOpacity={0.2}/>
                  <stop offset="95%" stopColor={lineColor} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <Line
                type="monotone"
                dataKey="value"
                stroke={lineColor}
                strokeWidth={2}
                dot={false}
                isAnimationActive={true}
                animationDuration={1000}
                animationEasing="ease-in-out"
                activeDot={{ r: 4, strokeWidth: 0, fill: lineColor }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="none"
                fill={`url(#${gradientId})`}
                isAnimationActive={true}
                animationDuration={1200}
              />
              <Tooltip 
                content={<CustomTooltip formatter={valueFormatter} />}
                animationDuration={200} 
                animationEasing="ease-out" 
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Fade>
    </Card>
  );
};

export default KpiChartCard;
